// Which environment this build belongs to, and what an attendee is told about
// it.
//
// The same shape as xp/kiosk/src/build/environment.ts and xp/app's server table,
// and the same argument: a name chosen from a closed list rather than a URL
// typed by hand, refused at build time rather than defaulted.
//
// WHY THE WRONG-SERVER FAILURE IS WORST HERE, and it is worth ranking them
// because the three surfaces are not equivalent. A tablet aimed at staging
// inconveniences a crew member, who notices because the screen says so. A kiosk
// aimed at staging shows rehearsal content on a two-metre screen, which the
// client sees. This surface aimed at staging takes a real member of the public's
// real name, company and email into a database that holds no real attendee data
// and is not treated as if it does — and nobody at the stand ever finds out,
// because the attendee walks away and the client's list is simply short. It is
// the only one of the three failures that is silent, involves a stranger's
// personal data, and cannot be corrected afterwards.
//
// Nothing here may touch `import.meta`, because vite.config.ts imports this
// module and runs it in Node.

export interface WebEnvironment {
  /** Whether this is a page a member of the public may be handed. */
  readonly isLive: boolean;
  /**
   * Where the service that answers this page is, or null for one that has a
   * name and does not exist.
   *
   * Null is refused rather than treated as permission, exactly as the tablet's
   * table and xp/xp.config.json refuse theirs. This surface never falls back to
   * its own origin: a relative request from a phone on a venue's guest wifi is
   * aimed at whatever served the page, which is quite possibly a captive portal
   * that would be delighted to receive somebody's contact details.
   */
  readonly baseUrl: string | null;
  /** What it is, for whoever is deploying. */
  readonly what: string;
}

// The same addresses xp/kiosk/src/build/environment.ts holds, because it is the
// same service: api/src/attend/routes.ts registers the attendee surface on the
// instance that already serves the screens.
//
// Restated rather than imported, on the rule those files share — xp/web depends
// on neither xp/app nor xp/kiosk and must not start — and check.mjs compares the
// tables and fails if a name in both has two addresses. That is what makes a copy
// a decision rather than a drift, and it earned its keep on the merge that
// brought these two surfaces together: this table said 4310 and the kiosk's said
// 8080, and the check named it in the first run.
//
// AND WHERE THIS TABLE DIVERGES FROM THE TABLET'S, WHICH IS `local`.
//
// xp/app points `local` at 4310 and is right to: that is xp/harness, which stands
// in for the *sync* server a tablet talks to. The harness has no database behind
// it, holds no signing key, and serves neither `/v1/attend/*` nor the screen
// routes — so a web build aimed there could not reach a single endpoint this
// package calls, and every attempt would read as the network being down rather
// than as the wrong address. `local` therefore means the API itself, running on
// this machine, at PORT's default in api/src/env.ts.
export const ENVIRONMENTS = {
  local: {
    isLive: false,
    baseUrl: "http://127.0.0.1:8080",
    what: "a fixture on the machine this build was made on",
  },
  staging: {
    isLive: false,
    baseUrl: "https://xp1-api-staging-739319167448.europe-west2.run.app",
    what: "the rehearsal service, which holds no real attendee data",
  },
  production: {
    // Filled 2026-09-12 with the production service's own run.app hostname,
    // the same string xp/app/src/server/environments.ts and xp.config.json
    // hold; check.mjs compares the tables. See the tablet's row for why it is
    // run.app and not api.novaexhibits.com.
    //
    // Served on the Mailgun sending domain from 2026-09-13: the join app
    // at the stand and magic-link paths, the holding page still at /.
    // A production build is now a page that can be published.
    baseUrl: "https://xp1-api-739319167448.europe-west2.run.app",
    isLive: true,
    what: "the live event",
  },
} as const satisfies Record<string, WebEnvironment>;

export type EnvironmentName = keyof typeof ENVIRONMENTS;

export const ENVIRONMENT_NAMES = Object.keys(ENVIRONMENTS) as EnvironmentName[];

/** The variable that names an environment. One string, so the check can find it. */
export const ENV_VARIABLE = "VITE_XP_ENV";

export type EnvironmentReading =
  | {
      readonly ok: true;
      readonly name: EnvironmentName;
      readonly isLive: boolean;
      readonly baseUrl: string;
    }
  | { readonly ok: false; readonly problem: string };

/**
 * Resolve the build-time variable into an environment, or into a refusal.
 *
 * Pure, and takes the value rather than reading it, so the build tool, the page
 * and the check all drive the same code over inputs they choose.
 */
export function readEnvironment(name: string | undefined): EnvironmentReading {
  const chosen = (name ?? "").trim();

  if (chosen === "") {
    return {
      ok: false,
      problem: `This build does not say which environment it belongs to. ${ENV_VARIABLE} was not set. The names are ${ENVIRONMENT_NAMES.join(", ")}.`,
    };
  }

  const known = (ENVIRONMENTS as Record<string, WebEnvironment | undefined>)[chosen];
  if (known === undefined) {
    return {
      ok: false,
      problem: `${ENV_VARIABLE} names ${JSON.stringify(chosen)}, which is not an environment this surface knows. The names are ${ENVIRONMENT_NAMES.join(", ")}.`,
    };
  }

  // The address alone decides. `isLive` used to refuse here as well, on the
  // reasoning that a live page with no server was a page that took details
  // nowhere; with an address on file the flag's only remaining job is to
  // silence the rehearsal notice, which rehearsalNotice reads.
  if (known.baseUrl === null) {
    return {
      ok: false,
      problem: `${ENV_VARIABLE} names ${chosen}, and there is no ${chosen} self-serve surface yet. Its address is null here, and a null there is refused rather than guessed at.`,
    };
  }

  return { ok: true, name: chosen as EnvironmentName, isLive: known.isLive, baseUrl: known.baseUrl };
}

/**
 * What the page says about which environment it is, or null where it should say
 * nothing.
 *
 * A SENTENCE RATHER THAN A CORNER MARK, and the difference from the kiosk is the
 * reader. The kiosk's mark is a word in a corner because the person who needs it
 * is a crew member setting a screen up and the person who must not be distracted
 * by it is a client walking past. Here the person reading is a member of the
 * public who is about to hand over their name and address, and the only honest
 * thing to tell them is what will happen to it — "this is a rehearsal, nothing
 * you type is kept" — in a full sentence, in the flow, where it cannot be missed.
 *
 * A build that resolved to nothing is marked too, so the rule has no exception
 * for anybody to remember. It is also the case that such a build cannot run:
 * vite.config.ts refuses it. Both, on the kiosk's reasoning about two locks.
 *
 * NOT DISMISSIBLE, AND THIS IS THE SURFACE WHERE THAT IS LEAST NEGOTIABLE. The
 * console's strip gained a close button on the reasoning that its reader has
 * been told, can see the page they are putting away, and will be told again on
 * the next load. None of that transfers. The reader here is a member of the
 * public about to type their name and address into a form, they are on this
 * page once, and what they are being told is not about a machine — it is what
 * will happen to their personal data. A sentence somebody can close before
 * they read it is a disclosure that did not happen.
 */
export function rehearsalNotice(reading: EnvironmentReading): string | null {
  if (!reading.ok) {
    return "This page was built without naming a server, so nothing you type here goes anywhere.";
  }
  if (reading.isLive) return null;
  return (
    `This is a rehearsal of the registration page (${reading.name}), not the real one. ` +
    "Anything you type is sent to a test service and is not kept."
  );
}
